import React, { useCallback, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useDetachedState } from '@/components/hooks/use-detached-state';

export interface GradientGlowContainerProps {
  color?: string;
  blur?: string;
  spread?: string;
  duration?: number;
}

export function GradientGlowContainer({
  color,
  blur,
  spread,
  duration = 30,
}: GradientGlowContainerProps) {
  const [, setTop, topRef] = useDetachedState(`0rem`);
  const [, setLeft, leftRef] = useDetachedState(`0rem`);

  const handler = useCallback(() => {
    setTop(`${Math.floor(Math.random() * 100)}vh`);
    setLeft(`${Math.floor(Math.random() * 100)}vw`);
  }, []);

  useEffect(() => {
    setTimeout(() => handler, 1000);
    const timer = setInterval(handler, (duration / 2) * 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Wrapper>
      <GlowPoint
        color={color}
        blur={blur}
        spread={spread}
        duration={duration}
        top={topRef.current}
        left={leftRef.current}
      />
      <GlowPoint
        color="rgba(158, 176, 214, 0.6)"
        blur={blur}
        spread={spread}
        duration={duration}
        top={`calc(90vh - ${topRef.current})`}
        left={`calc(100vw - ${leftRef.current})`}
        delay={duration / 2}
      />
    </Wrapper>
  );
}

interface GlowPointProps {
  color?: string;
  blur?: string;
  spread?: string;
  duration?: number;
  top?: string;
  left?: string;
  delay?: number;
  zIndex?: number;
  paused?: boolean;
  disableGrow?: boolean;
}

const glowAnim = keyframes`
  0% {
    opacity: 0.5;
    transform: scale(2);
  }
  50% {
    opacity: 0.4;
    transform: scale(4, 6);
  }
  50% {
    opacity: 0.4;
    transform: scale(6, 4);
  }
  100% {
    opacity: 0.5
  ;
    transform: scale(2);
  }
`;

export const Wrapper = styled.div`
  display: block;
  position: absolute;
  height: 100vh;
  width: 100vw;
  inset: 0;
  pointer-events: none;
  background: transparent;
  z-index: 0;
  transform: scaleX(-1);
`;

export const GlowPoint = styled.span<GlowPointProps>`
  position: absolute;
  display: block;
  top: ${(props) => props.top || `0`};
  left: ${(props) => props.left || `0`};

  height: 0.1rem;
  width: 0.1rem;
  z-index: ${(props) => props.zIndex || -2};
  border-radius: 100%;
  background: ${(props) => props.color || `#bfa7b7`};
  box-shadow: 0 0 ${(props) => props.blur || `30rem`}
    ${(props) => props.spread || `12rem`} ${(props) => props.color || `#bfa7b7`};
  animation: ${glowAnim} ${(props) => props.duration || 30}s ease-in-out;
  animation-iteration-count: infinite;
  animation-direction: reverse;
  animation-fill-mode: forwards;
  animation-delay: ${(props) => props.delay || 0}s;
  transition: top ${(props) => props.duration / 2 || 15}s ease-in-out,
    left ${(props) => props.duration / 2 || 15}s ease-in-out,
    box-shadow 1s ease-in-out;
  transform-origin: center center;
  animation-play-state: ${(props) => (props.paused ? `paused` : `running`)};
`;
